/**
 * Hartă pentru zonele de livrare. Clicurile adaugă colțurile zonei; zona închisă
 * primește tierul și prețul alese deasupra hărții.
 */
import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export interface ZoneDraft {
  name: string;
  priceCents: number;
  color: string;
  ring: Array<[number, number]>;
}

export function DeliveryZoneMap({
  zones,
  draft,
  onAddPoint,
  center,
  interactive = true,
  className = 'h-72',
}: {
  zones: ZoneDraft[];
  draft: Array<[number, number]>;
  onAddPoint?: (lng: number, lat: number) => void;
  center?: { lat: number; lng: number } | null;
  interactive?: boolean;
  className?: string;
}) {
  const holder = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const clickRef = useRef(onAddPoint);
  clickRef.current = onAddPoint;
  const centerRef = useRef(center);
  centerRef.current = center;

  useEffect(() => {
    const node = holder.current;
    if (!node || mapRef.current) return;
    const start = centerRef.current;
    const map = L.map(node, {
      center: start ? [start.lat, start.lng] : [45.94, 25.01],
      zoom: start ? 13 : 7,
      dragging: true,
      scrollWheelZoom: true,
      doubleClickZoom: true,
      boxZoom: true,
      keyboard: true,
      zoomControl: true,
    });
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    if (interactive) map.on('click', (event) => clickRef.current?.(event.latlng.lng, event.latlng.lat));
    mapRef.current = map;
    window.setTimeout(() => map.invalidateSize(), 200);
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const layer = layerRef.current;
    const map = mapRef.current;
    if (!layer || !map) return;
    layer.clearLayers();
    const bounds: Array<[number, number]> = [];
    for (const zone of zones) {
      if (zone.ring.length < 3) continue;
      const latLngs = zone.ring.map(([lng, lat]) => [lat, lng] as [number, number]);
      L.polygon(latLngs, { color: zone.color, weight: 2, fillColor: zone.color, fillOpacity: 0.28 })
        .bindTooltip(`${zone.name} · ${(zone.priceCents / 100).toFixed(2)} lei`)
        .addTo(layer);
      bounds.push(...latLngs);
    }
    if (draft.length) {
      const latLngs = draft.map(([lng, lat]) => [lat, lng] as [number, number]);
      L.polyline(latLngs, { color: '#0f3d33', weight: 2, dashArray: '4 4' }).addTo(layer);
      latLngs.forEach((point) => L.circleMarker(point, { radius: 4, color: '#0f3d33' }).addTo(layer));
    }
    if (bounds.length && !draft.length) map.fitBounds(bounds, { padding: [16, 16], maxZoom: 14 });
    else if (!draft.length && center) map.setView([center.lat, center.lng], 13);
  }, [zones, draft, center]);

  return <div ref={holder} className={`${className} w-full overflow-hidden rounded-[var(--radius-md)] border border-ink-200`} />;
}
